// Import react elements.
import { useContext, useEffect, useRef, useState } from "react";

// Import navigation elements.
import { Link, useSearchParams } from "react-router-dom";

// Import API calls.
import { getHelp } from "api/APIStrichCalls";

// Import context function.
import { AuthContext } from "global/context/AuthContext";

// Import hooks.
import usePageTitle from "global/hooks/usePageTitle";

// Import components.
import { LoadingSpinner } from "global/components/loading_ui/LoadingSpinnerAnimation";
import { FaqGrid } from "global/components/FaqGrid";

// Import css stylesheet.
import "./SupportPage.css";

// Define the available support tabs.
const supportTabs = [
    { type: "help", name: "Articles" },
    { type: "faq", name: "FAQs" },
    { type: "tutorial", name: "Tutorials" },
    { type: "contact", name: "Contact" },
];



// Prepare a short description from the trusted article HTML.
const getHelpPreview = (content) => {

    return String(content || "").replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();

};

const SupportPage = () => {

    // Set the page title.
    usePageTitle("Support");

    // Get the API token and current help link.
    const { token } = useContext(AuthContext);
    const [searchParams, setSearchParams] = useSearchParams();

    // Store each content type independently.
    const [helpSections, setHelpSections] = useState([]);
    const [faqSections, setFaqSections] = useState([]);
    const [tutorialSections, setTutorialSections] = useState([]);
    const [loading, setLoading] = useState(true);

    // Keep a reference to the selected section's content.
    const articleContentRef = useRef(null);



    // Load the published articles, FAQs and tutorials.
    useEffect(() => {

        // Wait until the token is available.
        if (!token) { return; }

        // Ignore responses after the page unmounts.
        let isMounted = true;

        const loadHelp = async () => {

            try {

                // Get all three content types through the same endpoint.
                const [helpResponse, faqResponse, tutorialResponse] = await Promise.all([
                    getHelp(token, "help"),
                    getHelp(token, "faq"),
                    getHelp(token, "tutorial"),
                ]);

                // Ignore late responses.
                if (!isMounted) { return; }

                // Store the returned sections.
                setHelpSections(helpResponse?.success && Array.isArray(helpResponse.results) ? helpResponse.results : []);
                setFaqSections(faqResponse?.success && Array.isArray(faqResponse.results) ? faqResponse.results : []);
                setTutorialSections(tutorialResponse?.success && Array.isArray(tutorialResponse.results) ? tutorialResponse.results : []);

            } catch (error) {

                console.error("Load Help Error:", error);

            } finally {

                // Finish loading unless the page has unmounted.
                if (isMounted) { setLoading(false); }

            }

        };

        // Start loading the support content.
        loadHelp();

        // Ignore in-flight responses if the page closes.
        return () => { isMounted = false; };

    }, [token]);



    // Resolve the active tab from the URL so direct links work.
    const requestedType = searchParams.get("type") || "help";
    const activeType = supportTabs.some((tab) => tab.type === requestedType) ? requestedType : "help";
    const activeTabIndex = supportTabs.findIndex((tab) => tab.type === activeType);
    const sectionCode = searchParams.get("section");
    const itemCode = searchParams.get("code");

    // Resolve the selected section and its ordered articles.
    const selectedSection = helpSections.find((section) => section.code === sectionCode) || helpSections[0] || null;
    const selectedItems = Array.isArray(selectedSection?.items) ? selectedSection.items : [];

    // Identify a specifically requested article for highlighting and scrolling.
    const selectedItem = selectedItems.find((item) => String(item.code || item.id) === itemCode) || null;



    // Scroll to the requested article once its section has been rendered.
    useEffect(() => {

        // Only scroll when a specific help section has been requested.
        if (loading || activeType !== "help" || !sectionCode || !selectedSection) { return; }

        // Find the requested article, or use the start of the section.
        const target = selectedItem
            ? document.getElementById(`support-help-item-${selectedItem.id}`)
            : articleContentRef.current;

        // Bring the requested content into view.
        target?.scrollIntoView({ behavior: "smooth", block: "start" });

    }, [loading, activeType, sectionCode, itemCode, selectedSection, selectedItem]);



    // Prepare all tutorial cards with their parent section code.
    const tutorialCards = tutorialSections.flatMap((section) => {

        // Return the published items from this section.
        const items = Array.isArray(section.items) ? section.items : [];
        return items.map((item) => ({ ...item, sectionCode: section.code, sectionName: section.name }));

    });

    // Look up a specific tutorial only when the URL requests one.
    const selectedTutorial = activeType === "tutorial" && sectionCode && itemCode
        ? tutorialCards.find((item) => item.sectionCode === sectionCode && String(item.code || item.id) === itemCode)
        : null;



    // Change the selected tab without retaining an unrelated section or item.
    const selectTab = (type) => {

        setSearchParams({ type });

    };



    // Display all articles belonging to the selected help section.
    const selectArticleSection = (section) => {

        // Return to the beginning when the section is already selected.
        if (sectionCode === section.code && !itemCode) {
            articleContentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
            return;
        }

        // Select the section without targeting an individual article.
        setSearchParams({ type: "help", section: section.code });

    };



    // Scroll to an article even when its URL is already selected.
    const scrollToArticle = (section, item) => {

        // Handle clicking the same article link more than once.
        if (sectionCode === section.code && itemCode === String(item.code || item.id)) {
            document.getElementById(`support-help-item-${item.id}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
        }

    };



    // Render the support page.
    return (

        <div className="support-page">

            {/* Use the Portal's existing tab styling. */}
            <div className="tabs-header support-page__tabs" role="tablist" aria-label="Help and Support">

                {/* Render the available support tabs. */}
                {supportTabs.map((tab) => (
                    <button
                        key={tab.type}
                        type="button"
                        role="tab"
                        aria-selected={activeType === tab.type}
                        className={`tabs-header__tab ${activeType === tab.type ? "tabs-header__tab--active" : ""}`}
                        onClick={() => selectTab(tab.type)}
                    >
                        {tab.name}
                    </button>
                ))}

                {/* Position the standard active-tab underline. */}
                <div
                    className="tabs-header__indicator"
                    style={{ width: `calc(100% / ${supportTabs.length})`, transform: `translateX(${activeTabIndex * 100}%)` }}
                />

            </div>

            {/* Show loading feedback for all content types. */}
            {loading ? (

                <div className="support-page__loading">
                    <LoadingSpinner message="Loading help..." />
                </div>

            ) : (

                <div className="support-page__tab-content">

                    {/* Display articles with expandable section navigation. */}
                    {activeType === "help" && (

                        helpSections.length === 0 ? <p>No help articles are available yet.</p> : (

                            <div className="support-page__help">

                                {/* Display the article navigation. */}
                                <nav className="support-page__help-navigation" aria-label="Help articles">
                                    <h2>Sections</h2>

                                    {/* Render each help section. */}
                                    {helpSections.map((section) => (
                                        <div className="support-page__help-section" key={section.id}>

                                            <button
                                                type="button"
                                                className={`support-page__section-button ${selectedSection?.id === section.id ? "support-page__section-button--selected" : ""}`}
                                                onClick={() => selectArticleSection(section)}
                                                aria-expanded={selectedSection?.code === section.code}
                                            >
                                                <span>{section.name}</span>
                                                <span aria-hidden="true">{selectedSection?.code === section.code ? "⌄" : "›"}</span>
                                            </button>

                                            {/* Show items only in the expanded section. */}
                                            {selectedSection?.code === section.code && (
                                                <ul className="support-page__help-items">

                                                    {/* Render each article link. */}
                                                    {(Array.isArray(section.items) ? section.items : []).map((item) => (
                                                        <li key={item.id}>
                                                            <Link
                                                                className={`support-page__help-link ${selectedSection?.id === section.id && selectedItem?.id === item.id ? "support-page__help-link--selected" : ""}`}
                                                                to={`/support?type=help&section=${encodeURIComponent(section.code)}&code=${encodeURIComponent(item.code || item.id)}`}
                                                                onClick={() => scrollToArticle(section, item)}
                                                                aria-current={selectedSection?.id === section.id && selectedItem?.id === item.id ? "location" : undefined}
                                                            >
                                                                {item.title}
                                                            </Link>
                                                        </li>
                                                    ))}

                                                </ul>
                                            )}

                                        </div>
                                    ))}

                                </nav>

                                {/* Render all articles in the selected section as one document. */}
                                {selectedSection && (
                                    <article className="support-page__help-article" ref={articleContentRef}>

                                        {/* Display the selected section heading. */}
                                        <p className="support-page__breadcrumb">Articles</p>
                                        <h2>{selectedSection.name}</h2>

                                        {/* Render each article in its existing order. */}
                                        {selectedItems.map((item) => (
                                            <section
                                                key={item.id}
                                                id={`support-help-item-${item.id}`}
                                                className="support-page__article-section"
                                            >
                                                <h3>{item.title}</h3>
                                                <div className="support-page__article-body" dangerouslySetInnerHTML={{ __html: item.content || "" }} />
                                            </section>
                                        ))}

                                    </article>
                                )}

                            </div>

                        )

                    )}

                    {/* Display FAQs as a full-width grid. */}
                    {activeType === "faq" && (
                        <section className="support-page__faqs">
                            {faqSections.length > 0 ? <FaqGrid sections={faqSections} /> : <p>No FAQs are available yet.</p>}
                        </section>
                    )}

                    {/* Display the requested tutorial or the tutorial card index. */}
                    {activeType === "tutorial" && (

                        selectedTutorial ? (

                            <article className="support-page__tutorial-article">
                                <Link to="/support?type=tutorial" className="support-page__back-link">← All tutorials</Link>
                                <p className="support-page__breadcrumb">Tutorials / {selectedTutorial.sectionName}</p>
                                <h2>{selectedTutorial.title}</h2>
                                <div className="support-page__article-body" dangerouslySetInnerHTML={{ __html: selectedTutorial.content || "" }} />
                            </article>

                        ) : (

                            tutorialCards.length > 0 ? (

                                <div className="support-page__tutorial-grid">

                                    {/* Render each tutorial as a card. */}
                                    {tutorialCards.map((item) => (
                                        <Link
                                            key={item.id}
                                            className="support-page__tutorial-card"
                                            to={`/support?type=tutorial&section=${encodeURIComponent(item.sectionCode)}&code=${encodeURIComponent(item.code || item.id)}`}
                                        >
                                            <span className="support-page__tutorial-category">{item.sectionName}</span>
                                            <h2>{item.title}</h2>
                                            <p>{getHelpPreview(item.content)}</p>
                                            <span className="support-page__tutorial-action">View tutorial →</span>
                                        </Link>
                                    ))}

                                </div>

                            ) : <p>No tutorials are available yet.</p>

                        )

                    )}

                    {/* Keep contacting Support separate from the documentation. */}
                    {activeType === "contact" && (
                        <section className="support-page__contact">
                            <h2>Contact Support</h2>
                            <p>Can't find the answer you're looking for? Email our support team and we'll be happy to help.</p>
                            <p><a href="mailto:support@strichsuite.com">support@strichsuite.com</a></p>
                        </section>
                    )}

                </div>

            )}

        </div>

    );

};

export default SupportPage;